import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { FC } from 'react';

import type { Field, FieldValue } from '@/api/types/field';
import { BinIcon } from '@/common/bin-icon';
import { TextField, TextFieldType } from '@/common/text-field';

interface ContentVariantTileProps {
    index: number;
    fields: Field[];
    fieldValues: FieldValue[];
    onChange: (text: string, fieldId: number, fieldValueId: number | undefined) => void;
    className?: string;
    isSelected?: boolean;
    onDelete: () => void;
    onClick?: () => void;
}

export const ContentVariantTile: FC<ContentVariantTileProps> = ({
    index,
    fields,
    fieldValues,
    onChange,
    className,
    isSelected,
    onDelete,
    onClick,
}) => {
    const [isError, setIsError] = useState(false)
    const [texts, setTexts] = useState<Record<number, string>>(
        fieldValues.reduce(
            (acc, fieldValue) => {
                acc[fieldValue.field as number] = fieldValue.value;
                return acc;
            },
            {} as Record<number, string>,
        ),
    );

    const handleTextChange = (value: string, fieldId: number) => {
        setTexts((prevTexts) => ({ ...prevTexts, [fieldId]: value }));

        if (value !== '') {
            const fieldValue = fieldValues.find((fv) => fv.field === fieldId);
            onChange(value, fieldId, fieldValue?.id);
            setIsError(false);
        } else {
            setIsError(true);
        }
    };

    return (
        <div
            className={`flex flex-col gap-2 ${className}`}
            onClick={onClick}
            onKeyDown={(e) => e.key === 'Enter' && onClick && onClick()}
        >
            <div className="flex flex-row justify-between">
                <p className="text-sm text-gray-100">Version {index + 1}</p>
                <button type="button" onClick={onDelete}>
                    <BinIcon className="stroke-red-50 hover:opacity-70" />
                </button>
            </div>
            <div
                className={`bg-gray-130 rounded p-3 flex flex-col gap-5 ${isSelected && !isError ? 'border-blue-60 border-solid border-[1px]' : isError ? 'border-red-50 border-solid border-[1px]' : ''}`}
            >
                {fields.map((field) => {
                    const fieldId = field.id as number;
                    return (
                        <div key={field.id} className="flex flex-col">
                            <div className="flex flex-row justify-between">
                                <p className="text-sm text-white">{field.name}</p>
                                {field.properties.charLimit > 0 && (
                                    <p className="text-sm text-gray-110">
                                        {field.properties.charLimit - (texts[fieldId] || '').length} characters left
                                    </p>
                                )}
                            </div>
                            <TextField
                                className="mt-1"
                                id="text-f"
                                type={TextFieldType.Text}
                                value={texts[fieldId] || ''}
                                maxLength={field.properties.charLimit > 0 ? field.properties.charLimit : undefined}
                                onChange={(value) => handleTextChange(value, fieldId)}
                                placeholder="Enter Text"
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
